
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Calendar as CalendarIcon, X } from "lucide-react";
import { format, addDays, isBefore, startOfDay, startOfToday } from "date-fns";
import { useAvailableTimeSlots } from "@/hooks/useAvailableTimeSlots";
import { BookingDetail } from "@/hooks/useBooking";
import { calculateUTCBookingTimes } from "@/utils/timezoneUtils";

interface RescheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: BookingDetail;
  onReschedule: (newDate: string, newStartTime: string, newEndTime: string, timeSlotId?: string) => void;
  isLoading?: boolean;
}

export const RescheduleDialog = ({ 
  open, 
  onOpenChange, 
  booking, 
  onReschedule, 
  isLoading = false 
}: RescheduleDialogProps) => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<any>(null);

  const { data: availableSlots, isLoading: slotsLoading } = useAvailableTimeSlots(
    booking.service_id,
    selectedDate
  );

  // Reset selections when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedDate(undefined);
      setSelectedTimeSlot(null);
    }
  }, [open]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setSelectedTimeSlot(null); // Reset time slot when date changes
  };

  const handleTimeSlotSelect = (slot: any) => {
    setSelectedTimeSlot(slot);
  };

  const handleConfirmReschedule = () => {
    console.log('=== RESCHEDULE BUTTON CLICKED ===');
    console.log('selectedDate:', selectedDate);
    console.log('selectedTimeSlot:', selectedTimeSlot);
    console.log('onReschedule function:', typeof onReschedule);
    
    if (!selectedDate || !selectedTimeSlot) {
      console.log('Missing selection - selectedDate:', !!selectedDate, 'selectedTimeSlot:', !!selectedTimeSlot);
      return;
    }

    try {
      console.log('Processing reschedule with time slot:', selectedTimeSlot);

      const newDate = format(selectedDate, 'yyyy-MM-dd');
      console.log('Formatted new date:', newDate);
      
      // Get duration from the service object
      const durationMinutes = selectedTimeSlot.service?.duration_minutes;
      console.log('Duration minutes from service:', durationMinutes);
      
      if (!durationMinutes) {
        console.error('No duration found in service object:', selectedTimeSlot.service);
        return;
      }

      // Check if calculateUTCBookingTimes function exists
      if (typeof calculateUTCBookingTimes !== 'function') {
        console.error('calculateUTCBookingTimes is not a function:', calculateUTCBookingTimes);
        return;
      }

      // Extract UTC times from the time slot data using timezone utils
      const timeResult = calculateUTCBookingTimes(
        selectedTimeSlot.start_time, 
        durationMinutes
      );
      
      console.log('calculateUTCBookingTimes result:', timeResult);
      
      if (!timeResult || !timeResult.startTime || !timeResult.endTime) {
        console.error('Invalid time result from calculateUTCBookingTimes:', timeResult);
        return;
      }

      const { startTime, endTime } = timeResult;
      console.log('Extracted UTC times:', { startTime, endTime });

      console.log('Calling onReschedule with:', { 
        newDate, 
        startTime, 
        endTime, 
        timeSlotId: selectedTimeSlot.id 
      });

      onReschedule(newDate, startTime, endTime, selectedTimeSlot.id);
      console.log('onReschedule called successfully');
      
    } catch (error) {
      console.error('Error in handleConfirmReschedule:', error);
    }
  };

  const formatTimeSlot = (startTime: string, endTime: string) => {
    const start = format(new Date(startTime), 'h:mm a');
    const end = format(new Date(endTime), 'h:mm a');
    return `${start} - ${end}`;
  };

  // Allow dates from today onwards for rescheduling
  const today = startOfToday();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2" />
            Reschedule Your Session
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current booking info */}
          <Card className="bg-gray-50">
            <CardContent className="p-4">
              <h3 className="font-medium text-gray-900 mb-2">Current Booking</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p><strong>Service:</strong> {booking.service_name}</p>
                <p><strong>Date:</strong> {format(new Date(booking.booking_date), 'MMMM d, yyyy')}</p>
                <p><strong>Time:</strong> {format(new Date(`2000-01-01T${booking.start_time}`), 'h:mm a')} - {format(new Date(`2000-01-01T${booking.end_time}`), 'h:mm a')}</p>
                <p><strong>Location:</strong> {booking.location}</p>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Date Selection */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Select New Date</h3>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                disabled={(date) => isBefore(date, today)}
                className="rounded-md border pointer-events-auto"
              />
            </div>

            {/* Time Slot Selection */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">
                Select New Time
                {selectedDate && (
                  <span className="text-sm font-normal text-gray-600 ml-2">
                    for {format(selectedDate, 'MMM d, yyyy')}
                  </span>
                )}
              </h3>
              
              {!selectedDate ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Please select a date first</p>
                </div>
              ) : slotsLoading ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Loading available times...</p>
                </div>
              ) : !availableSlots || availableSlots.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <X className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No available time slots for this date</p>
                  <p className="text-sm">Please try a different date</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {availableSlots.map((slot) => (
                    <Button
                      key={slot.id}
                      variant={selectedTimeSlot?.id === slot.id ? "default" : "outline"}
                      className="w-full justify-start"
                      onClick={() => handleTimeSlotSelect(slot)}
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      {formatTimeSlot(slot.start_time, slot.end_time)}
                      <Badge variant="secondary" className="ml-auto">
                        {slot.max_bookings - slot.current_bookings} available
                      </Badge>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmReschedule}
              disabled={!selectedDate || !selectedTimeSlot || isLoading}
              className="flex-1"
            >
              {isLoading ? "Rescheduling..." : "Confirm Reschedule"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
